import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Anchor, Badge, Group, Text, Tooltip } from "@mantine/core";
import { PageShell } from "@/components/PageShell";
import { Section } from "@/components/Section";
import { LoadingState, ErrorState } from "@/components/LoadingState";
import { SortableTable, Column } from "@/components/SortableTable";
import { OpenNowBadge } from "@/components/OpenNowBadge";
import { StoreFilterBar } from "@/components/StoreFilterBar";
import {
  useStoresWithMeta,
  retailerNames,
  type StoreWithMeta,
} from "@/lib/queries";
import { filterStores, type StoreStatus } from "@/lib/storeSearch";
import { fmt, isOpenNow, localTimeIn } from "@/lib/format";

export function StoreListPage() {
  const query = useStoresWithMeta();

  const [search, setSearch] = useState("");
  const [timezone, setTimezone] = useState<string | null>(null);
  const [retailers, setRetailers] = useState<string[]>([]);
  const [status, setStatus] = useState<StoreStatus | null>(null);

  const retailerOptions = useMemo(
    () => retailerNames(query.data?.stores ?? []),
    [query.data],
  );

  // Without inventory every `active` is null, so the control would
  // silently empty the table whichever way it was set.
  const statusKnown = useMemo(
    () => (query.data?.stores ?? []).some((store) => store.active !== null),
    [query.data],
  );

  const timezones = useMemo(() => {
    const set = new Set<string>();
    for (const store of query.data?.stores ?? []) set.add(store.timezone);
    return [...set].sort();
  }, [query.data]);

  // If these ever disagree, the fleet is being truncated somewhere between the
  // API and this table — say so rather than quietly showing a short list.
  const shortfall = query.data
    ? query.data.count - query.data.stores.length
    : 0;

  const rows = useMemo(
    () =>
      filterStores(query.data?.stores ?? [], {
        search,
        timezone,
        retailers,
        status,
      }),
    [query.data, search, timezone, retailers, status],
  );

  const columns: Column<StoreWithMeta>[] = useMemo(
    () => [
      {
        key: "name",
        label: "Store",
        get: (row) => row.name,
        render: (row) => (
          <Anchor component={Link} to={`/store/${row.id}`} size="sm">
            {row.name}
          </Anchor>
        ),
      },
      {
        key: "site_id",
        label: "Site ID",
        get: (row) => row.site_id,
        render: (row) => (
          <Text size="sm" c="dimmed" ff="monospace">
            {row.site_id}
          </Text>
        ),
      },
      {
        key: "retailer",
        label: "Retailer",
        get: (row) => row.retailer?.name,
        render: (row) => <Text size="sm">{row.retailer?.name ?? "—"}</Text>,
      },
      {
        key: "timezone",
        label: "Timezone",
        get: (row) => row.timezone,
        render: (row) => <Text size="sm">{row.timezone}</Text>,
      },
      {
        key: "local_time",
        label: "Local time",
        get: (row) => localTimeIn(row.timezone),
        render: (row) => (
          <Text size="sm" ff="monospace">
            {localTimeIn(row.timezone) ?? "—"}
          </Text>
        ),
      },
      {
        key: "hours",
        label: "Hours",
        get: (row) => row.opening_time,
        render: (row) => (
          <Text size="sm" ff="monospace">
            {fmt.hourRange(row.opening_time, row.closing_time)}
          </Text>
        ),
      },
      {
        key: "status",
        label: "Status",
        // Sort by what the badge actually shows, not by timezone: open first,
        // then closed, with an unusable timezone sorting last.
        get: (row) => {
          const open = isOpenNow(
            row.timezone,
            row.opening_time,
            row.closing_time,
          );
          // Null sorts last: an unusable timezone is neither open nor closed.
          if (open === null) return null;
          return open ? 0 : 1;
        },
        render: (row) => (
          <OpenNowBadge
            timezone={row.timezone}
            openingTime={row.opening_time}
            closingTime={row.closing_time}
          />
        ),
      },
      {
        key: "oos_interval",
        label: "OOS batch",
        get: (row) => row.oos_event_batch_interval,
        render: (row) => (
          <Text size="sm">{fmt.duration(row.oos_event_batch_interval)}</Text>
        ),
      },
      {
        key: "flags",
        label: "Flags",
        get: (row) => (row.needs_coverage_qa ? 1 : 0),
        render: (row) => (
          <Group gap={4} wrap="nowrap">
            {row.active === false ? (
              <Badge color="gray" variant="light" size="sm">
                Inactive
              </Badge>
            ) : null}
            {row.needs_coverage_qa ? (
              <Tooltip label="Coverage QA required" withArrow>
                <Badge color="yellow" variant="light" size="sm">
                  Coverage QA
                </Badge>
              </Tooltip>
            ) : null}
          </Group>
        ),
      },
    ],
    [],
  );

  return (
    <PageShell
      title="Stores"
      subtitle={
        query.data
          ? `${rows.length} of ${query.data.count} stores`
          : "Opening hours, timezones and OOS batch cadence"
      }
    >
      <StoreFilterBar
        search={search}
        onSearchChange={setSearch}
        retailerOptions={retailerOptions}
        retailers={retailers}
        onRetailersChange={setRetailers}
        status={status}
        onStatusChange={setStatus}
        statusKnown={statusKnown}
        timezoneOptions={timezones}
        timezone={timezone}
        onTimezoneChange={setTimezone}
      />
      {query.isLoading ? <LoadingState label="Loading stores…" /> : null}
      {query.error ? <ErrorState error={query.error} /> : null}
      {shortfall > 0 ? (
        <Alert color="yellow" title="Store list may be incomplete" mb="lg">
          The API reports {query.data?.count} stores but only{" "}
          {query.data?.stores.length} were returned across{" "}
          {query.data?.pagesFetched} page(s). {shortfall} are missing from this
          table.
        </Alert>
      ) : null}
      {query.data ? (
        <Section title="All stores">
          <SortableTable
            columns={columns}
            rows={rows}
            rowKey="id"
            initialSortKey="name"
            initialSortDir={1}
            pageSize={25}
            emptyMessage="No stores match those filters."
          />
        </Section>
      ) : null}
    </PageShell>
  );
}
